/** @jsxImportSource preact */
import { type ComponentChildren, createContext, type VNode } from "preact"
import {
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks"
import type {
  EventHandler,
  EventNode,
  Mediator,
  TypedEvent,
} from "#js/util/component-hierarchy"

const PreactComponentHierarchyContext =
  createContext<EventNode<TypedEvent> | null>(null)

export function PreactComponentScope(props: {
  node: EventNode<TypedEvent>
  children: ComponentChildren
}): VNode {
  return (
    <PreactComponentHierarchyContext.Provider value={props.node}>
      {props.children}
    </PreactComponentHierarchyContext.Provider>
  )
}

/** Preact roots are separate from React roots, so their element node is passed explicitly. */
export function usePreactComponentNode(
  handler?: EventHandler<TypedEvent>,
): EventNode<TypedEvent> {
  const parent = useContext(PreactComponentHierarchyContext)
  if (!parent)
    throw new Error("Preact presenters require an explicit component node")
  const latestHandler = useRef(handler)
  // Child custom elements can emit events during insertion, before layout effects.
  latestHandler.current = handler
  const node = useMemo(() => parent.createChild(), [parent])
  useMemo(
    () =>
      node.subscribe(
        (event, context) =>
          latestHandler.current?.(event, context) ?? "unhandled",
      ),
    [node],
  )
  useLayoutEffect(() => (): void => node.dispose(), [node])
  return node
}

export function usePreactMediatorSnapshot<
  State,
  Event extends TypedEvent,
  Effect,
>(mediator: Mediator<State, Event, Effect>): State {
  const [, setRevision] = useState(0)
  useLayoutEffect(() => {
    const refresh = (): void => setRevision((revision) => revision + 1)
    const unsubscribe = mediator.subscribe(refresh)
    // A swapped mediator may change during child insertion back to the old value.
    refresh()
    return unsubscribe
  }, [mediator])
  // Read the current mediator during render, including the first render after a swap.
  return mediator.getSnapshot()
}
